import { useEffect, useMemo, useRef, useState } from 'react';
import Sheet from '../components/Sheet';
import Icon from '../components/Icon';
import { addDays, parseISODate, weekStart } from '../lib/dates';
import { drawSchedule, fileName, peopleOf } from '../lib/scheduleImage';
import { useApp } from '../lib/AppContext';

const MODES = [{ id: 'all', label: 'Cả tuần của tôi' }, { id: 'person', label: '1 học viên' }, { id: 'free', label: 'Lịch trống' }];
const ORIENTATIONS = [{ id: 'landscape', label: 'Ngang · in A4' }, { id: 'portrait', label: 'Dọc · điện thoại' }];
const dm = (iso) => { const d = parseISODate(iso); return `${d.getDate()}/${d.getMonth() + 1}`; };
const native = () => !!window.Capacitor?.isNativePlatform?.();

const toBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

// The phone's share sheet with the picture (it has "Lưu hình ảnh" and "In" too); false where files can't be shared.
async function shareFile(file, title) {
  if (!navigator.canShare?.({ files: [file] })) return false;
  try {
    await navigator.share({ files: [file], title });
  } catch (e) {
    if (e?.name !== 'AbortError') throw e;
  }
  return true;
}

function download(file) {
  const url = URL.createObjectURL(file);
  const a = Object.assign(document.createElement('a'), { href: url, download: file.name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The picture alone in a hidden frame, printed on A4 in its orientation.
function printImage(dataUrl, orientation) {
  const frame = Object.assign(document.createElement('iframe'), { title: 'In lịch tập' });
  Object.assign(frame.style, { position: 'fixed', width: '0', height: '0', border: '0', right: '0', bottom: '0' });
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>@page{size:A4 ${orientation};margin:10mm}html,body{margin:0}
    img{width:100%;height:auto;display:block}</style></head><body><img src="${dataUrl}"></body></html>`);
  doc.close();
  const img = doc.querySelector('img');
  const go = () => { frame.contentWindow.focus(); frame.contentWindow.print(); setTimeout(() => frame.remove(), 1000); };
  if (img.complete) go(); else img.onload = go;
}

// "Chia sẻ lịch tuần": the shown week as a picture, for the whole week, one person (to send them), or when the
// coach is still free (no names). Landscape prints on A4; portrait reads on a phone. "Ẩn tên học viên" replaces
// names before sending the week to someone else. Lưu ảnh / In / Chia sẻ work on the website and in the app.
export default function ShareScheduleSheet({ open, week, appointments, onClose }) {
  const { api, profile } = useApp();
  const monday = weekStart(week);
  const [mode, setMode] = useState('all');
  const [orientation, setOrientation] = useState('landscape');
  const [hideNames, setHideNames] = useState(false);
  const [personKey, setPersonKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);
  const canvas = useRef(null);
  const people = useMemo(() => peopleOf(appointments || []), [appointments]);
  const person = people.find((p) => p.key === personKey) || people[0];

  useEffect(() => { if (open) { setNote(null); setPreview(null); } }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    drawSchedule({ canvas: (canvas.current ||= document.createElement('canvas')), week: monday, appointments, mode, personKey: person?.key, personName: person?.name,
      hideNames: mode === 'all' && hideNames, orientation, coachName: profile?.name || '' })
      .then((c) => { if (current) setPreview(c.toDataURL('image/png')); });
    return () => { current = false; };
  }, [open, monday, appointments, mode, person?.key, person?.name, hideNames, orientation, profile?.name]);

  const file = async () => new File([await toBlob(canvas.current)], fileName(monday, mode), { type: 'image/png' });
  const title = `Lịch tập tuần ${dm(monday)} – ${dm(addDays(monday, 6))}`;
  const run = async (key, fn) => {
    setBusy(key);
    setNote(null);
    try {
      await fn();
      // For the admin user page (UserEvent "schedule_exported"); never waited on, never shown.
      api.post('/events', { kind: 'schedule_exported', mode: 'coach', data: { range: mode } }, { quiet: true }).catch(() => {});
    } catch (e) {
      setNote(e.message || 'Không thực hiện được, thử lại nhé');
    } finally {
      setBusy(null);
    }
  };
  // On a phone the share sheet is where "Lưu hình ảnh" is; on a computer the file downloads.
  const save = () => run('save', async () => {
    const f = await file();
    const touch = native() || window.matchMedia?.('(pointer: coarse)').matches;
    if (!(touch && (await shareFile(f, title)))) { download(f); setNote('Đã lưu ảnh'); }
  });
  // The app's web view can't print by itself: the share sheet has "In".
  const print = () => run('print', async () => {
    if (native() && (await shareFile(await file(), title))) return;
    printImage(preview, orientation);
  });
  const share = () => run('share', async () => {
    const f = await file();
    if (!(await shareFile(f, title))) { download(f); setNote('Máy này không chia sẻ được ảnh, đã lưu ảnh để bạn gửi'); }
  });

  const chip = (on) => `h-10 rounded-[12px] text-sm font-semibold ${on ? 'bg-base-100 shadow-sm' : 'text-muted'}`;
  const empty = mode !== 'free' && (appointments || []).length === 0;
  return (
    <Sheet open={open} onClose={onClose} title={`Chia sẻ lịch tuần ${dm(monday)} – ${dm(addDays(monday, 6))}`}>
      <div className="flex flex-col gap-4">
        <div role="tablist" aria-label="Chia sẻ gì" className="grid grid-cols-3 gap-1 rounded-[16px] bg-base-300/60 p-1">
          {MODES.map((m) => <button key={m.id} type="button" role="tab" aria-selected={mode === m.id} className={chip(mode === m.id)} onClick={() => setMode(m.id)}>{m.label}</button>)}
        </div>
        {mode === 'person' && (
          people.length > 0 ? (
            <div className="-mx-5 flex gap-2 overflow-x-auto px-5" role="radiogroup" aria-label="Học viên">
              {people.map((p) => (
                <button key={p.key} type="button" role="radio" aria-checked={person?.key === p.key} onClick={() => setPersonKey(p.key)}
                  className={`h-9 shrink-0 rounded-full px-3.5 text-[13px] font-semibold ${person?.key === p.key ? 'bg-[#15261f] text-white' : 'border border-line bg-base-100'} ${p.offline ? 'border-dashed' : ''}`}>
                  {p.name}
                </button>
              ))}
            </div>
          ) : <p className="text-sm text-muted">Tuần này chưa có buổi tập nào.</p>
        )}
        <div className="overflow-hidden rounded-[14px] border border-line bg-base-200">
          {preview ? <img src={preview} alt={`Xem trước: ${title}`} className="block max-h-[38dvh] w-full object-contain" />
            : <div className="skeleton h-48 w-full" />}
        </div>
        {empty && <p className="text-xs text-muted">Tuần này chưa có buổi tập nào, ảnh sẽ là lịch trống.</p>}
        <div role="radiogroup" aria-label="Khổ ảnh" className="grid grid-cols-2 gap-2">
          {ORIENTATIONS.map((o) => (
            <button key={o.id} type="button" role="radio" aria-checked={orientation === o.id} onClick={() => setOrientation(o.id)}
              className={`h-12 rounded-[14px] border-2 text-sm font-bold ${orientation === o.id ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
              {o.label}
            </button>
          ))}
        </div>
        {mode === 'all' && (
          <label className="flex min-h-12 items-center justify-between gap-3">
            <span>
              <span className="block text-[15px] font-semibold">Ẩn tên học viên</span>
              <span className="block text-xs text-muted">Bật khi gửi cho người khác</span>
            </span>
            <input type="checkbox" className="toggle toggle-primary" checked={hideNames} onChange={(e) => setHideNames(e.target.checked)} />
          </label>
        )}
        <div className="grid grid-cols-3 gap-2">
          {[['save', 'download', 'Lưu ảnh', save], ['print', 'printer', 'In', print], ['share', 'upload', 'Chia sẻ', share]].map(([key, icon, label, fn]) => (
            <button key={key} type="button" disabled={!preview || !!busy} onClick={fn} className="flex flex-col items-center gap-1.5 text-xs font-semibold">
              <span className="grid size-12 place-items-center rounded-[14px] bg-base-200">
                {busy === key ? <span className="loading loading-spinner loading-sm" /> : <Icon name={icon} className="size-6" />}
              </span>
              {label}
            </button>
          ))}
        </div>
        {note && <p role="status" className="text-center text-sm font-semibold text-brand-text">{note}</p>}
      </div>
    </Sheet>
  );
}
